import React from 'react';
import { API_URL } from '../config';
import type { InstanceLogsStreamState, InstanceLogStreamEvent } from '@chatsdet/types';

const initialState: InstanceLogsStreamState = {
  status: 'idle',
  logs: [],
};

type UseInstanceLogsOptions = {
  /** Maximum number of log lines to keep in buffer. Default: 1000 */
  maxLogs?: number;
  /** Auto-connect when instanceId is provided. Default: true */
  autoConnect?: boolean;
};

type UseInstanceLogsReturn = {
  /** Current stream state */
  state: InstanceLogsStreamState;
  /** Whether the stream is connected */
  isConnected: boolean;
  /** Manually connect to the stream */
  connect: () => void;
  /** Manually disconnect from the stream */
  disconnect: () => void;
  /** Clear the log buffer */
  clearLogs: () => void;
};

/**
 * Hook for subscribing to instance container logs via SSE.
 *
 * @param instanceId - The environment instance ID to stream logs from
 * @param options - Optional configuration
 * @returns Stream state and control functions
 *
 * @example
 * ```tsx
 * const { state, isConnected, connect, disconnect } = useInstanceLogs(instanceId);
 *
 * return (
 *   <InstanceLogViewer
 *     state={state}
 *     isConnected={isConnected}
 *     onConnect={connect}
 *     onDisconnect={disconnect}
 *   />
 * );
 * ```
 */
export function useInstanceLogs(
  instanceId: string | null,
  options?: UseInstanceLogsOptions
): UseInstanceLogsReturn {
  const { maxLogs = 1000, autoConnect = true } = options ?? {};

  const [state, setState] = React.useState<InstanceLogsStreamState>(initialState);
  const [isConnected, setIsConnected] = React.useState(false);
  const eventSourceRef = React.useRef<EventSource | null>(null);

  const handleEvent = React.useCallback(
    (event: InstanceLogStreamEvent) => {
      setState((prev) => {
        if (event.type === 'instance.log') {
          return {
            ...prev,
            logs: [...prev.logs.slice(-(maxLogs - 1)), event.data],
          };
        }

        if (event.type === 'instance.log.status') {
          const newStatus =
            event.data.status === 'streaming'
              ? 'streaming'
              : event.data.status === 'stopped'
                ? 'stopped'
                : event.data.status === 'error'
                  ? 'error'
                  : prev.status;

          return {
            ...prev,
            status: newStatus,
            error: event.data.message,
          };
        }

        return prev;
      });
    },
    [maxLogs]
  );

  const connect = React.useCallback(() => {
    if (!instanceId) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Update status to connecting
    setState((prev) => ({ ...prev, status: 'connecting' }));

    // Create new EventSource connection
    const eventSource = new EventSource(
      `${API_URL}/org/environments/instances/${instanceId}/logs/stream`,
      { withCredentials: true }
    );

    eventSourceRef.current = eventSource;

    // Handle instance log event types
    const eventTypes = ['instance.log', 'instance.log.status'];

    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          handleEvent({ type: eventType, data } as InstanceLogStreamEvent);
        } catch (err) {
          console.error(`Failed to parse ${eventType} event:`, err);
        }
      });
    });

    eventSource.onopen = () => {
      setIsConnected(true);
      setState((prev) => ({ ...prev, status: 'streaming' }));
    };

    eventSource.onerror = () => {
      setIsConnected(false);
      setState((prev) => {
        // Don't overwrite 'stopped' with 'error'
        if (prev.status === 'stopped') {
          return prev;
        }
        return { ...prev, status: 'error' };
      });
      eventSource.close();
    };
  }, [instanceId, handleEvent]);

  const disconnect = React.useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    setState((prev) => ({ ...prev, status: 'stopped' }));
  }, []);

  const clearLogs = React.useCallback(() => {
    setState((prev) => ({ ...prev, logs: [] }));
  }, []);

  // Auto-connect when instanceId changes (if autoConnect is enabled)
  React.useEffect(() => {
    if (instanceId && autoConnect) {
      connect();
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [instanceId, autoConnect, connect]);

  // Reset state when instanceId changes
  React.useEffect(() => {
    if (!instanceId) {
      setState(initialState);
      setIsConnected(false);
    }
  }, [instanceId]);

  return {
    state,
    isConnected,
    connect,
    disconnect,
    clearLogs,
  };
}
